import { useEffect, useState } from "react";
import api from "../../lib/api";
import { authenticatedDownload } from "../../lib/pdf";
import {
  Download, Printer, RefreshCw, Mail, ChevronDown, ChevronRight,
  Building2, Users,
} from "lucide-react";
import { toast } from "sonner";
import EmailSreoDialog from "./EmailSreoDialog";

const fmtMoney = (v) =>
  `$${Math.round(Number(v || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

/**
 * Combined Schedule of Real Estate Owned + current-month Rent Roll — nested
 * display where each property card shows the SREO row and its per-unit rent
 * roll status stacked together.
 */
export default function CombinedSreoRentRoll({ companyName = "", companyLogo = "" } = {}) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({}); // { property_id: true }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/combined-sreo-rent-roll");
      setPayload(data);
    } catch (err) {
      toast.error("Failed to load combined report — " + (err.response?.status || "network error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDownload = async () => {
    if (!payload) return;
    setDownloading(true);
    try {
      await authenticatedDownload(
        "/api/reports/combined-sreo-rent-roll/pdf",
        `combined-sreo-rent-roll_${payload.as_of}.pdf`,
      );
    } catch {
      toast.error("PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  const onPrint = () => window.print();

  const toggle = (pid) => setCollapsed((s) => ({ ...s, [pid]: !s[pid] }));
  const collapseAll = () => {
    const all = {};
    (payload?.rows || []).forEach((r) => { all[r.property_id] = true; });
    setCollapsed(all);
  };
  const expandAll = () => setCollapsed({});

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-16 text-center text-slate-400">
        Loading combined SREO + Rent Roll…
      </div>
    );
  }

  if (!payload || (payload.rows || []).length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-16 text-center">
        <Building2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <div className="text-sm font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
          No properties in your portfolio yet
        </div>
        <div className="text-sm text-slate-500 mt-1">
          Add at least one property with active leases to generate this report.
        </div>
      </div>
    );
  }

  const rows = payload.rows || [];
  const t = payload.totals || {};
  const rr = payload.rent_roll_totals || {};
  const portfolioCap = t.market_value && t.market_value > 0
    ? t.annual_noi / t.market_value
    : null;

  return (
    <div data-testid="combined-sreo-rent-roll">
      {/* Actions (hidden when printing) */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6 no-print flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="overline text-slate-500">Combined report · As of</div>
          <div className="text-sm font-bold text-slate-900 font-mono mt-0.5">{payload.as_of}</div>
          <div className="text-xs text-slate-500 mt-1">
            {payload.properties} {payload.properties === 1 ? "property" : "properties"} · {rr.active_units || 0} active unit{rr.active_units === 1 ? "" : "s"} · {rr.month_label} rent roll nested per property
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={expandAll} className="btn-outline-dark text-xs">
            Expand all
          </button>
          <button type="button" onClick={collapseAll} className="btn-outline-dark text-xs">
            Collapse all
          </button>
          <button
            type="button"
            onClick={load}
            data-testid="combined-refresh-btn"
            className="btn-outline-dark flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            type="button"
            onClick={onPrint}
            data-testid="combined-print-btn"
            className="btn-outline-dark flex items-center gap-2 text-sm"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            data-testid="combined-email-lender-btn"
            className="btn-outline-dark flex items-center gap-2 text-sm"
            title="Email the combined PDF to your lender contacts"
          >
            <Mail className="w-4 h-4" /> Email to lender
          </button>
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            data-testid="combined-download-pdf-btn"
            className="btn-primary-dark flex items-center gap-2 text-sm disabled:opacity-60"
          >
            <Download className="w-4 h-4" /> {downloading ? "Generating…" : "Download PDF"}
          </button>
        </div>
      </div>

      {/* Printable body */}
      <div className="combined-print-root">
        <div className="print-header hidden print:block mb-6">
          {companyLogo && (
            <img
              src={companyLogo}
              alt={companyName || "Company logo"}
              className="h-12 w-auto object-contain mb-2"
              data-testid="combined-print-logo"
            />
          )}
          {companyName && (
            <div className="text-xs text-slate-700 uppercase tracking-widest font-bold" data-testid="combined-print-company">
              {companyName}
            </div>
          )}
          <div className={`text-xs uppercase tracking-widest font-bold ${companyName ? "text-slate-400 mt-0.5" : "text-slate-500"}`}>
            Combined SREO + Rent Roll
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>
            As of {payload.as_of}
          </h1>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="combined-kpis">
          <Kpi label="Market value" value={fmtMoney(t.market_value)} />
          <Kpi label="Loan balance" value={fmtMoney(t.outstanding_balance)} bad={t.outstanding_balance > 0} />
          <Kpi label="Total equity" value={fmtMoney(t.equity)} good={t.equity > 0} />
          <Kpi label={`${rr.month_label || "Month"} expected`} value={fmtMoney(rr.expected)} />
          <Kpi label={`${rr.month_label || "Month"} collected`} value={fmtMoney(rr.collected)} good={(rr.collected || 0) > 0} />
          <Kpi
            label="Collection rate"
            value={fmtPct(rr.collection_rate)}
            good={(rr.collection_rate || 0) >= 90}
            bad={(rr.collection_rate || 0) < 50}
          />
        </div>

        {/* Per-property nested cards */}
        <div className="space-y-4" data-testid="combined-property-cards">
          {rows.map((row) => {
            const isCollapsed = !!collapsed[row.property_id];
            const propRR = row.rent_roll || {};
            const units = propRR.units || [];
            const capr = row.market_value > 0 ? row.annual_noi / row.market_value : null;

            return (
              <div
                key={row.property_id}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden"
                data-testid={`combined-card-${row.property_id}`}
              >
                {/* Property header — name + address + collapse toggle */}
                <button
                  type="button"
                  onClick={() => toggle(row.property_id)}
                  className="w-full text-left px-5 py-4 bg-slate-50 border-b border-slate-200 flex items-start gap-3 hover:bg-slate-100 transition-colors no-print"
                  data-testid={`combined-toggle-${row.property_id}`}
                >
                  {isCollapsed
                    ? <ChevronRight className="w-5 h-5 mt-0.5 text-slate-400 shrink-0" />
                    : <ChevronDown className="w-5 h-5 mt-0.5 text-slate-400 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate" style={{ fontFamily: "Outfit" }}>
                      {row.name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {row.address || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
                    <span>
                      <span className="overline">Value</span>
                      <span className="ml-1.5 font-mono font-bold text-slate-900">{fmtMoney(row.market_value)}</span>
                    </span>
                    <span>
                      <span className="overline">Collected</span>
                      <span className={`ml-1.5 font-mono font-bold ${(propRR.month_collected || 0) > 0 ? "text-green-700" : "text-slate-400"}`}>
                        {fmtMoney(propRR.month_collected)}/{fmtMoney(propRR.month_expected)}
                      </span>
                    </span>
                  </div>
                </button>

                {/* Print-only property header */}
                <div className="hidden print:block px-5 py-3 border-b border-slate-200 bg-slate-50">
                  <div className="font-bold text-slate-900">{row.name}</div>
                  <div className="text-xs text-slate-500">{row.address || "—"}</div>
                </div>

                {/* Body — SREO summary + rent roll, or collapsed */}
                {!isCollapsed && (
                  <div className="p-5 print:block space-y-4">
                    {/* SREO stat row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 text-sm">
                      <Stat label="Type" value={row.property_type || "—"} kind="overline" />
                      <Stat label="Units" value={row.units || 0} />
                      <Stat label="Purchase date" value={fmtDate(row.purchase_date)} mono />
                      <Stat label="Purchase" value={fmtMoney(row.purchase_price)} mono />
                      <Stat label="Current value" value={fmtMoney(row.market_value)} mono bold />
                      <Stat label="NOI (yr)" value={fmtMoney(row.annual_noi)} mono />
                      <Stat label="Cap rate" value={fmtPct(capr != null ? capr * 100 : null)} mono />
                      <Stat
                        label="Equity"
                        value={fmtMoney(row.equity)}
                        mono bold
                        tone={(row.equity || 0) >= 0 ? "good" : "bad"}
                      />
                    </div>

                    {/* Loan balance full-width callout if non-zero */}
                    {row.outstanding_balance > 0 && (
                      <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs font-mono rounded-sm px-3 py-2 flex items-center justify-between">
                        <span className="font-semibold">
                          Loan balance {row.lender ? `· ${row.lender}` : ""}
                        </span>
                        <span className="font-bold">{fmtMoney(row.outstanding_balance)}</span>
                      </div>
                    )}

                    {/* Rent roll mini-table */}
                    <div>
                      <div className="flex items-center justify-between mb-2 pb-1 border-b border-slate-200">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-400" />
                          <div className="text-sm font-semibold text-slate-900">
                            Rent roll · {rr.month_label}
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 font-mono">
                          {units.length} unit{units.length === 1 ? "" : "s"} · {fmtPct(propRR.collection_rate)} collected
                        </div>
                      </div>

                      {units.length === 0 ? (
                        <div className="text-xs text-slate-400 italic py-3 text-center border border-dashed border-slate-200 rounded-sm">
                          No active units this month.
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid={`combined-rr-table-${row.property_id}`}>
                            <thead className="bg-slate-100 border-b border-slate-200">
                              <tr className="text-left">
                                <Th>Unit</Th>
                                <Th>Tenant</Th>
                                <Th className="text-right">Monthly rent</Th>
                                <Th className="text-right">Expected</Th>
                                <Th className="text-right">Collected</Th>
                                <Th className="text-right">Outstanding</Th>
                                <Th>Status</Th>
                              </tr>
                            </thead>
                            <tbody>
                              {units.map((u) => (
                                <tr
                                  key={u.lease_id}
                                  className="border-b border-slate-100 hover:bg-slate-50"
                                  data-testid={`combined-rr-row-${u.lease_id}`}
                                >
                                  <Td className="font-mono">{u.unit_label || "—"}</Td>
                                  <Td>
                                    <div className="font-semibold text-slate-900">{u.tenant_name}</div>
                                    <div className="text-xs text-slate-400">{u.tenant_email}</div>
                                  </Td>
                                  <Td className="text-right font-mono">{fmtMoney(u.monthly_rent)}</Td>
                                  <Td className="text-right font-mono text-slate-600">{fmtMoney(u.expected)}</Td>
                                  <Td className="text-right font-mono font-bold text-slate-900">{fmtMoney(u.collected)}</Td>
                                  <Td className={`text-right font-mono font-bold ${u.outstanding > 0 ? "text-red-600" : "text-green-600"}`}>
                                    {fmtMoney(u.outstanding)}
                                  </Td>
                                  <Td>
                                    <span className={`overline ${u.paid ? "text-green-600" : "text-amber-600"}`}>
                                      {u.paid ? "Paid" : "Outstanding"}
                                    </span>
                                  </Td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-slate-900 text-white">
                                <Td colSpan={3} className="font-bold">
                                  {row.name} · totals
                                </Td>
                                <Td className="text-right font-mono font-bold">{fmtMoney(propRR.month_expected)}</Td>
                                <Td className="text-right font-mono font-bold">{fmtMoney(propRR.month_collected)}</Td>
                                <Td className="text-right font-mono font-bold">{fmtMoney(propRR.month_outstanding)}</Td>
                                <Td className="font-mono font-bold">{fmtPct(propRR.collection_rate)}</Td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Grand totals footer */}
        <div className="mt-6 bg-slate-900 text-white rounded-sm p-5" data-testid="combined-grand-totals">
          <div className="overline text-slate-400">Portfolio totals</div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-3">
            <FooterStat label="Market value" value={fmtMoney(t.market_value)} />
            <FooterStat label="Loan balance" value={fmtMoney(t.outstanding_balance)} tone="amber" />
            <FooterStat label="Total equity" value={fmtMoney(t.equity)} tone="green" />
            <FooterStat label={`${rr.month_label} collected`} value={fmtMoney(rr.collected)} />
            <FooterStat label="Collection rate" value={fmtPct(rr.collection_rate)} />
            <FooterStat label="Portfolio cap rate" value={portfolioCap != null ? fmtPct(portfolioCap * 100) : "—"} />
          </div>
        </div>

        <div className="mt-4 text-xs text-slate-500">
          NOI is computed from active-lease rents minus year-to-date expenses (including stored annual taxes + insurance).
          Rent roll collected amounts come from Stripe-confirmed rent payments dated within {rr.month_label}.
          Loan balance = original principal minus recorded principal paid on the property's primary loan.
        </div>
      </div>

      <EmailSreoDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        endpoint="/reports/combined-sreo-rent-roll/email"
        title="Email combined report to lender"
        subtitle="Attaches the branded PDF with SREO + current-month rent roll"
      />
    </div>
  );
}

function Kpi({ label, value, good, bad }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="overline text-slate-500 truncate" title={label}>{label}</div>
      <div
        className={`mt-2 text-xl font-bold tracking-tight font-mono ${
          good ? "text-green-600" : bad ? "text-red-600" : "text-slate-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function Stat({ label, value, mono, bold, kind, tone }) {
  let cls = "text-sm text-slate-900";
  if (mono) cls += " font-mono";
  if (bold) cls += " font-bold";
  if (tone === "good") cls += " text-green-700";
  if (tone === "bad") cls += " text-red-600";
  return (
    <div>
      <div className="overline text-slate-500">{label}</div>
      <div className={`mt-0.5 ${cls}`}>
        {kind === "overline"
          ? <span className="overline text-slate-700">{value}</span>
          : value}
      </div>
    </div>
  );
}

function FooterStat({ label, value, tone }) {
  const toneCls =
    tone === "amber" ? "text-amber-300" :
    tone === "green" ? "text-green-300" : "text-white";
  return (
    <div>
      <div className="overline text-slate-400 truncate" title={label}>{label}</div>
      <div className={`mt-1 text-lg font-bold font-mono ${toneCls}`}>{value}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 overline text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "", colSpan }) {
  return (
    <td colSpan={colSpan} className={`px-3 py-2 ${className}`}>
      {children}
    </td>
  );
}
